import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, LogOut, User, Globe } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Breadcrumbs } from './Breadcrumbs';
import { format } from 'date-fns';
import { is as isLocale, enUS } from 'date-fns/locale';

interface HeaderProps {
  onMenuClick: () => void;
}

export function Header({ onMenuClick }: HeaderProps) {
  const { t, i18n } = useTranslation();
  const { provider, office, logout } = useAuth();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const currentDate = format(new Date(), 'EEEE, d. MMMM yyyy', {
    locale: i18n.language === 'is' ? isLocale : enUS,
  });

  const toggleLanguage = () => {
    const newLang = i18n.language === 'is' ? 'en' : 'is';
    i18n.changeLanguage(newLang);
  };

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-30 bg-white border-b border-gray-200 shadow-soft">
      <div className="flex items-center justify-between px-4 lg:px-6 h-16">
        {/* Left section */}
        <div className="flex items-center space-x-4">
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Open menu"
          >
            <Menu className="h-6 w-6 text-gray-700" />
          </button>

          <div className="hidden lg:block">
            <Breadcrumbs />
          </div>
        </div>

        {/* Center section - Office name */}
        {office && (
          <div className="hidden md:flex items-center space-x-2 text-sm">
            <span className="text-gray-500">{office.name}</span>
            <span className="px-2 py-1 bg-brand-light text-brand-primary text-xs font-medium rounded">
              {office.subscription_tier}
            </span>
          </div>
        )}

        {/* Right section */}
        <div className="flex items-center space-x-4">
          {/* Current date */}
          <div className="hidden md:block text-sm text-gray-600">
            {currentDate}
          </div>

          {/* Language toggle */}
          <button
            onClick={toggleLanguage}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label={`Switch to ${i18n.language === 'is' ? 'English' : 'Icelandic'}`}
          >
            <Globe className="h-5 w-5 text-gray-600" />
            <span className="sr-only">
              {i18n.language === 'is' ? 'EN' : 'IS'}
            </span>
          </button>

          {/* User menu */}
          <div className="relative">
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-expanded={isUserMenuOpen}
              aria-haspopup="true"
            >
              <div className="w-8 h-8 bg-gradient-to-br from-brand-primary to-brand-secondary rounded-full flex items-center justify-center">
                <span className="text-white font-semibold text-sm">
                  {provider?.name
                    .split(' ')
                    .map((n) => n[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2) || 'U'}
                </span>
              </div>
              <span className="hidden lg:block text-sm font-medium text-gray-700">
                {provider?.name}
              </span>
            </button>

            {/* Dropdown menu */}
            {isUserMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setIsUserMenuOpen(false)}
                  aria-hidden="true"
                />
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-strong border border-gray-200 py-2 z-50 animate-slide-down">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900">
                      {provider?.name}
                    </p>
                    <p className="text-xs text-gray-500">{provider?.email}</p>
                    <p className="text-xs text-gray-500 mt-1">{provider?.role}</p>
                  </div>

                  <button
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      // Navigate to profile (to be implemented)
                    }}
                    className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <User className="h-4 w-4" />
                    <span>Notandaupplýsingar</span>
                  </button>

                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-error hover:bg-red-50 transition-colors"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>{t('auth.logout')}</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile breadcrumbs */}
      <div className="lg:hidden px-4 pb-3">
        <Breadcrumbs />
      </div>
    </header>
  );
}
